import { Link as RouterLink, useMatches } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { AppBar, Box, IconButton, MenuItem, Select, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import {
  ApartmentOutlined as ClientIcon,
  HelpOutline as HelpIcon,
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AccountPopover from '../../Components/AppBar/AccountPopover';
import LanguagePopover from '../../Components/AppBar/LanguagePopover';
import useAuth from '../../Context/Hooks/useCustomAuth';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import GatewayLogo from '../../Assets/images/logo-web-gateway.png';
import { SIDEBAR_WIDTH, TOPBAR_HEIGHT } from '../layoutConstants';

// Bordered icon button matching mockup style (32px square, 1px border, rounded corners)
const TopbarIconButton = ({ title, children, onClick }) => {
  const theme = useTheme();
  return (
    <Tooltip
title={title}
arrow
    >
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 32,
          height: 32,
          border: `1px solid ${theme.palette.brand?.iconBorder ?? '#E5E7EB'}`,
          borderRadius: '6px',
          bgcolor: 'background.paper',
          color: theme.palette.brand?.iconGlyph ?? '#7A7A7A',
          p: 0,
          '&:hover': {
            bgcolor: 'background.default',
            borderColor: theme.palette.primary.light,
          },
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
};
TopbarIconButton.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
};

const DashboardNavbar = ({ onSidebarOpen }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const matches = useMatches();
  const { user } = useAuth();
  const { clients, selectedClient, loadClients, selectClient } = useGatewayContagem();

  useEffect(() => {
    if (!clients.length) loadClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first client when list loads and nothing is selected
  useEffect(() => {
    if (clients.length && !selectedClient) selectClient(clients[0]);
  }, [clients]); // eslint-disable-line react-hooks/exhaustive-deps

  // Current page label from deepest matched route that has a crumb handle
  const crumbKey = matches.filter(m => m.handle?.crumb).slice(-1)[0]?.handle?.crumb ?? '';
  const pageName = crumbKey ? t(crumbKey) : '';

  const currentPath = matches.at(-1)?.pathname ?? '';
  const isAdminRoute = ['/Gateway/Users', '/Gateway/Clients', '/Gateway/Devices', '/Gateway/Stores/', '/Gateway/Ingestion'].some(
    p => currentPath.startsWith(p)
  );

  const userName = user?.name ?? '';
  const userRole = user?.jobTitle ?? '';

  const topbarBorder = theme.palette.brand?.topbarBorder ?? '#EFF0F2';
  const crumbParent = theme.palette.brand?.breadcrumbParent ?? '#8C8D8F';
  const crumbSeparator = theme.palette.brand?.breadcrumbSeparator ?? '#A1A1A1';
  const crumbActive = theme.palette.brand?.breadcrumbActive ?? '#2A2B2D';

  const platformName = t('common:siteTitle', { defaultValue: 'Gateway' });
  const browserTitle = pageName ? `${platformName} · ${pageName}` : platformName;

  return (
    <>
    <Helmet>
      <title>{browserTitle}</title>
    </Helmet>
    <AppBar
      sx={{
        backgroundColor: 'background.paper',
        boxShadow: 'none',
        borderBottom: `1px solid ${topbarBorder}`,
        zIndex: theme.zIndex.drawer,
        color: crumbActive,
        height: TOPBAR_HEIGHT,
      }}
    >
      <Toolbar
        disableGutters
        sx={{ height: TOPBAR_HEIGHT, minHeight: `${TOPBAR_HEIGHT}px !important`, px: 0 }}
      >
        {/* ── Zone 1: Logo (fixed sidebar-column width) ── */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            height: '100%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            borderRight: `1px solid ${topbarBorder}`,
          }}
        >
          <RouterLink
to="/Gateway/Dashboard"
style={{ display: 'flex', alignItems: 'center' }}
          >
            <Box
              component="img"
              src={GatewayLogo}
              alt="Gateway"
              sx={{ width: 108, height: 'auto', display: 'block' }}
            />
          </RouterLink>

          {/* Hamburger: only shown on mobile (sidebar is persistent on desktop) */}
          <IconButton
            onClick={onSidebarOpen}
            size="small"
            sx={{
              display: { xs: 'flex', lg: 'none' },
              ml: 'auto',
              color: crumbActive,
            }}
          >
            <MenuIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* ── Zone 2: Breadcrumb (content area start) ── */}
        <Box sx={{ pl: 3, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Stack
direction="row"
alignItems="center"
spacing={0.625}
          >
            <Typography
              component="span"
              sx={{ fontSize: '0.8125rem', fontWeight: 400, color: crumbParent, lineHeight: 1 }}
            >
              Gateway
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '0.8125rem', color: crumbSeparator, lineHeight: 1, mx: 0.25 }}
            >
              ›
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '0.8125rem', fontWeight: 600, color: crumbActive, lineHeight: 1 }}
            >
              {pageName}
            </Typography>
          </Stack>
        </Box>

        {/* ── Client selector (GATEWAY_ADMIN only — other roles are auto-scoped to their client) ── */}
        {!isAdminRoute && clients.length > 0 && user?.role === 'GATEWAY_ADMIN' && (
          <Box sx={{ ml: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClientIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
            <Select
              size="small"
              value={selectedClient?.id ?? ''}
              onChange={e => {
                const client = clients.find(c => c.id === e.target.value) ?? null;
                selectClient(client);
              }}
              displayEmpty
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                minWidth: 160,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '& .MuiSelect-select': { py: '5px' },
              }}
            >
              {clients.map(c => (
                <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8125rem' }}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* ── Spacer ── */}
        <Box sx={{ flex: 1 }} />

        {/* ── Zone 3: Action cluster ── */}
        <Stack
direction="row"
alignItems="center"
spacing={1}
sx={{ pr: 1.75 }}
        >
          {/* Search, Notifications, Help — hidden until wired to real functionality */}
          <Box sx={{ display: 'none' }}>
          <TopbarIconButton title="Pesquisar">
            <SearchIcon sx={{ fontSize: 16 }} />
          </TopbarIconButton>
          <TopbarIconButton title="Notificações">
            <NotificationsIcon sx={{ fontSize: 16 }} />
          </TopbarIconButton>
          <TopbarIconButton title="Ajuda">
            <HelpIcon sx={{ fontSize: 16 }} />
          </TopbarIconButton>
          </Box>

          {/* Language switcher (only when multiple locales configured) */}
          {process.env.REACT_APP_AVAILABLE_LANGUAGES?.split(';').length > 1 && (
            <LanguagePopover />
          )}

          {/* User section — avatar + name + role, triggers account dropdown */}
          <AccountPopover
name={userName}
department={userRole}
          />
        </Stack>
      </Toolbar>
    </AppBar>
    </>
  );
};

DashboardNavbar.propTypes = {
  onSidebarOpen: PropTypes.func,
};

export default DashboardNavbar;
